import { ChangeEvent, FocusEvent } from 'react';
import { memo } from 'react';
import { useKeyboardFormField } from '../../../context/Keyboard/keyboard';
import { useFormikContext, useField } from 'formik';
import { BaseTextField } from '../BaseTextField/BaseTextField';
import { KeyboardLayouts } from '../../SimpleKeyboard/Layout';
import { GoMotoComponent } from '../../../API/GoMoto.types';
import { FormRoNumberFieldProps } from './FormRoNumberField.types';
import { useScrollToOnError } from '../../../helpers/useScrollTo';
import { TextFieldValidation } from '../../../helpers/FormFieldValidation';
import { useKioskState } from '../../../context/Kiosk/kiosk';
import { useLocale } from '../../../context/Localization/localization';
import { KeyboardFormFieldProps } from '../../../context/Keyboard/keyboard.types';

/**
 * This TextField wrapper handles limiting the RO Number field to 20 characters on both Web and Kiosk versions.
 * The RO Number is treated as a string since it can be alpha-numeric.
 *
 * @param name - {@link FormRoNumberFieldProps.name name} description.
 * @param id - {@link FormRoNumberFieldProps.id id} description.
 * @param title - {@link FormRoNumberFieldProps.title title} description.
 * @param label - {@link FormRoNumberFieldProps.label label} description.
 * @param ariaLabel - {@link FormRoNumberFieldProps.ariaLabel ariaLabel} description.
 * @param required - {@link FormRoNumberFieldProps.required required} description. (default: false)
 * @param autoFocus - {@link FormRoNumberFieldProps.autoFocus autoFocus} description. (default: false)
 * @param keyboardLayout - {@link FormRoNumberFieldProps.keyboardLayout keyboardLayout} description. (default: KeyboardLayouts.AlphanumericNumpad)
 * @param shiftAfterSpace - {@link FormRoNumberFieldProps.shiftAfterSpace shiftAfterSpace} description. (default: false)
 * @param shiftAfterPeriod - {@link FormRoNumberFieldProps.shiftAfterPeriod shiftAfterPeriod} description. (default: false)
 * @param shiftAfterAmpersand - {@link FormRoNumberFieldProps.shiftAfterAmpersand shiftAfterAmpersand} description. (default: false)
 * @param hideKeyboardOnClickOut - {@link FormRoNumberFieldProps.hideKeyboardOnClickOut hideKeyboardOnClickOut} description. (default: false)
 * @param capsLock - {@link FormRoNumberFieldProps.capsLock capsLock} description. (default: false)
 * @param fullWidth - {@link FormRoNumberFieldProps.fullWidth fullWidth} description. (default: false)
 * @param seventyFivePercentWidth - {@link FormRoNumberFieldProps.seventyFivePercentWidth seventyFivePercentWidth} description. (default: false)
 * @param onChangeCallback - {@link FormRoNumberFieldProps.onChangeCallback onChangeCallback} description.
 * @param onBlurCallback - {@link FormRoNumberFieldProps.onBlurCallback onBlurCallback} description.
 */
const FormRoNumberField: GoMotoComponent<FormRoNumberFieldProps> = memo(
    ({
        name,
        id,
        title,
        label,
        ariaLabel,
        disabled = false,
        required = false,
        autoFocus = false,
        keyboardLayout = KeyboardLayouts.AlphanumericNumpad,
        shiftAfterSpace = false,
        shiftAfterPeriod = false,
        shiftAfterAmpersand = false,
        hideKeyboardOnClickOut = false,
        capsLock = false,
        fullWidth = false,
        seventyFivePercentWidth = false,
        onChangeCallback,
        onBlurCallback,
    }) => {
        /**
         * Not all formik validation accounts for TextFields that are null.
         * So even though we pass an undefined validation type to the function,
         * it is still required in order to have null required fields validate.
         */
        const [field, meta] = useField<string>({
            name,
            validate: TextFieldValidation({
                required: required ?? false,
                label,
                name,
                validationType: undefined,
            }),
        });
        const { submitCount, setFieldValue } = useFormikContext();
        const locale = useLocale();
        const {
            dealer: { primaryLanguage },
        } = useKioskState();

        const onInputChangeCallback: KeyboardFormFieldProps['onInputChangeCallback'] = ({ value, caretPosition }) => {
            if (value.length > 20) {
                value = value.slice(0, caretPosition - 1) + value.slice(caretPosition);
                caretPosition = caretPosition - 1;
            }

            return {
                value: value,
                caretPosition: caretPosition,
            };
        };

        const { inputRef, onBlur, onFocus, onChange } = useKeyboardFormField({
            name,
            value: field.value ?? '',
            keyboardLayout,
            shiftAfterAmpersand,
            shiftAfterPeriod,
            shiftAfterSpace,
            hideKeyboardOnClickOut,
            capsLock,
            setFieldValue,
            onChangeCallback,
            onBlurCallback,
            onInputChangeCallback,
        });
        useScrollToOnError({ scrollable: inputRef,
            name: name });

        const handleOnFocus = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            if (event.target.value !== field.value) {
                setFieldValue(name, event.target.value);
            }

            onFocus(event);
        };

        const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            onChange({ value: event.target.value,
                caretPosition: event.target.selectionEnd ?? 0 });
        };

        const handleOnBlur = () => {
            onBlur();
        };

        return (
            <BaseTextField
                name={name}
                id={id}
                title={title}
                label={label}
                ariaLabel={ariaLabel}
                inputMode={'text'}
                type={'text'}
                value={field.value ?? ''}
                inputRef={inputRef}
                error={meta.error}
                touched={meta.touched || submitCount > 0}
                disabled={disabled}
                required={required}
                autoFocus={autoFocus}
                fullWidth={fullWidth}
                seventyFivePercentWidth={seventyFivePercentWidth}
                capsLock={capsLock}
                onFocus={handleOnFocus}
                onChange={handleOnChange}
                onBlur={handleOnBlur}
                inputProps={{
                    maxLength: 20,
                }}
                safariHack={locale === primaryLanguage ? 'primarylang' : 'alternatelang'}
            />
        );
    },
);

export default FormRoNumberField;